/**
 * Several notes (W2-06; DESIGN.md §4.11 "14 NOTES (9 Tap, 3 Drag, 2 Hold) on 2 lines"): the counts header,
 * mixed-value fields with per-item expressions (NoteInspector), how many of them are simultaneous with another
 * note (Phira multi-highlight), and the Distribute / Transform tools (W2-11, through `registry.run`).
 */

import { useMemo } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import type { ChartDoc, Note, NoteId, NoteKind } from '../../../core/model/types';
import type { TempoMap } from '../../../core/time/types';
import { useLocale, useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { Button, IconButton, Section } from '../../../ui';
import { useDocValue, useTempo, useTopicStamp } from '../hooks';
import { m } from '../inspector.messages';
import { fmtList } from '../labels';
import { arrayEq, kindCounts, NOTE_KINDS, noteSec, sortedIds } from '../model';
import { Actions, Readout, Title, useStore } from '../parts';
import { KIND_COUNT, NoteInspector, selectNotes } from './NoteSection';

export function MultiNoteSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.notes);
  if (store === null || ids.length < 2) return null;
  return <MultiNoteBody services={props.services} store={store} ids={ids} />;
}

/** Counts by kind, lines and notes simultaneous with another note (Phira multi-highlight is chart-wide). */
function summarize(all: ChartDoc['notes'], notes: readonly Note[], tempo: TempoMap, _stamp: number) {
  const bySec = new Map<number, number>();
  for (const n of all.values()) {
    const sec = noteSec(tempo, n);
    bySec.set(sec, (bySec.get(sec) ?? 0) + 1);
  }
  let simultaneous = 0;
  for (const n of notes) if ((bySec.get(noteSec(tempo, n)) ?? 0) > 1) simultaneous++;
  return { counts: kindCounts(notes), lines: new Set(notes.map((n) => n.line)).size, simultaneous };
}

function MultiNoteBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly NoteId[];
}) {
  const { services, store, ids } = props;
  const t = useT();
  const locale = useLocale();
  const idsKey = ids.join(',');
  const selector = useMemo(
    () => selectNotes(idsKey === '' ? [] : (idsKey.split(',').map(Number) as NoteId[])),
    [idsKey],
  );
  const notes = useDocValue(store, selector, ['notes'], arrayEq);
  const notesStamp = useTopicStamp(store, 'notes');
  const all = store.doc.notes;
  const tempo = useTempo(store);
  // Recomputed whenever any note changed (the table is updated in place, hence the stamp).
  const summary = useMemo(() => summarize(all, notes, tempo, notesStamp), [all, notes, tempo, notesStamp]);

  const detail = t(m.notesDetail, {
    kinds: fmtList(
      locale,
      NOTE_KINDS.filter((k: NoteKind) => summary.counts[k] > 0).map((k) =>
        t(KIND_COUNT[k], { count: summary.counts[k] }),
      ),
    ),
    lines: t(m.onLines, { count: summary.lines }),
  });

  return (
    <Section
      title={<Title main={t(m.notesTitle, { count: notes.length })} detail={detail} />}
      actions={
        <>
          <IconButton icon="Copy" size="sm" label={{ key: m.copyNotes }} command="edit.copy" />
          <IconButton icon="Trash2" size="sm" label={{ key: m.deleteNotes }} command="edit.delete" />
        </>
      }
    >
      <NoteInspector services={services} ids={ids} />
      {summary.simultaneous > 0 && (
        <Readout testId="notes-simultaneous">{t(m.notesSimultaneous, { count: summary.simultaneous })}</Readout>
      )}
      <Actions>
        <Button size="sm" icon="AlignHorizontalSpaceAround" command="tools.distribute">
          {t(m.distribute)}
        </Button>
        <Button size="sm" icon="Move" command="transform.open">
          {t(m.transform)}
        </Button>
      </Actions>
    </Section>
  );
}

export const multiNoteSectionDef: InspectorSectionDef = {
  id: 'multiNote',
  order: 101,
  level: 'guided',
  appliesTo: (sel) => sel.notes.size >= 2,
  component: MultiNoteSection,
};
